import React, { FC, useEffect, useState } from "react";
import { MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../constants/command-status";
import { WS_RETRIES, WS_URL } from "../../constants/ws-constants";
import { useGlobalState } from "../../Providers";
import { getSecureWebsocketIca, getUnsecureWebsocketIca } from "../../utilities/UrlUtils";
import { IcaWebsocketContext } from "./IcaWebsocketContext";

interface IcaWebsocketContextProviderType {
    user: any;
}

export const IcaWebsocketContextProvider: FC<IcaWebsocketContextProviderType> = ({ children, user }) => {
    const [websocket, setWebsocket] = useState<WebSocket>();
    const [connectionState, setConnectionState] = useState<boolean>(false);
    const { buildInfo, loadBuildInfo } = useGlobalState() ?? {};
    let retry = 0;
    let timeout = WS_RETRIES.TIMEOUT_DEFAULT;
    const [engineState, setEngineState] = useState<{ isStarted: boolean, user: string, dateTimeStamp: string }>({
        isStarted: true,
        user: '',
        dateTimeStamp: ''
    });

    // Load build info on component mount
    useEffect(() => {
        loadBuildInfo && loadBuildInfo();
    }, [])

    useEffect(() => {
        if (websocket === undefined) {
            setConnectionState(false);
        }
        else if (websocket.readyState === websocket.OPEN) {
            setConnectionState(true);
        }
        else {
            setConnectionState(false);
        }
    }, [websocket?.readyState])

    // When buildInfo changes, initialize the websocket connection if serverAddress is available
    useEffect(() => {
        const token = sessionStorage.getItem("token");

        if ((websocket === undefined || websocket.readyState === websocket.CLOSED) && token !== null && buildInfo?.serverAddress !== undefined) {
            initializeConnection();
        }
    }, [buildInfo])

    const initializeConnection = () => {
        const token = sessionStorage.getItem("token")?.replaceAll('"', '');
        let tempWebsocket: WebSocket | undefined;
        let connectInterval;
        if ((tempWebsocket === undefined || tempWebsocket.readyState === WebSocket.CLOSED) && (retry < WS_RETRIES.MAX_RETRY && token !== null)) {
            retry = retry + 1;
            timeout = timeout + WS_RETRIES.TIMEOUT_INCREASE;
            const accessTokenQuery = token !== null ? `?access_token=${token}` : '';
            const url = window.location.protocol === "https:" ? getSecureWebsocketIca(buildInfo?.serverAddress ?? WS_URL.LOCALHOST_WEBSOCKET_NAME, buildInfo?.securePort ?? WS_URL.SECURE_WEBSOCKET_PORT) : getUnsecureWebsocketIca(buildInfo?.serverAddress ?? WS_URL.LOCALHOST_WEBSOCKET_NAME, buildInfo?.unsecurePort ?? WS_URL.UNSECURE_WEBSOCKET_PORT);
            tempWebsocket = new WebSocket(url + accessTokenQuery);
        }
        else if (retry >= WS_RETRIES.MAX_RETRY) {
            console.log(`Failed to connect to websocket. Exceeded maximum retry attempts of `, WS_RETRIES.MAX_RETRY);
            return;
        }

        tempWebsocket!.onopen = () => {
            setConnectionState(true);
            retry = 0;
            timeout = WS_RETRIES.TIMEOUT_DEFAULT;
            clearTimeout(connectInterval);
        }

        tempWebsocket!.onclose = (e) => {
            setConnectionState(false);
            if (e.wasClean) {
                clearTimeout(connectInterval);
                return;
            }
            connectInterval = setTimeout(initializeConnection, timeout);
        }

        setWebsocket(tempWebsocket);
    }

    const disposeConnection = () => {
        stopAllTopics();
        websocket?.close();
    }

    const startStopTopic = (command: string, name: string, state: string, custom?: string) => {
        if (websocket !== undefined && websocket.readyState === WebSocket.OPEN) {
            const data = {
                command: command,
                attributes: {
                    name: name,
                    value: state,
                    custom: custom
                }
            }
            websocket?.send(JSON.stringify(data))
        }
    }

    const stopAllTopics = () => {
        if (websocket !== undefined && websocket.readyState === WebSocket.OPEN) {
            const NAME_MONITORING = MONITORING_COMMAND.MONITORING;
            const STATE_STOP = MONITORING_COMMAND_STATE.MONITORING_STOP;
            startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, NAME_MONITORING, STATE_STOP);
            startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, NAME_MONITORING, STATE_STOP);
            startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, NAME_MONITORING, STATE_STOP);
            startStopTopic(MONITORING_COMMAND.MONITORING_TASKS, NAME_MONITORING, STATE_STOP);
            startStopTopic(MONITORING_COMMAND.MONITORING_ENGINE, NAME_MONITORING, STATE_STOP);
            startStopTopic(MONITORING_COMMAND.MONITORING_DISCOVERY, NAME_MONITORING, STATE_STOP);
            startStopTopic(MONITORING_COMMAND.MONITORING_QUEUE, NAME_MONITORING, STATE_STOP);
        }
    }

    const setOnMessage = (onmessage: (data: any) => void) => {
        if (websocket && websocket.readyState === websocket.OPEN) {
            websocket.onmessage = (data: any) => {
                //start - this logic needs to be moved out of here(possible in the view that set the engine state menu-layout.tsx)
                if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
                    const response = JSON.parse(data.data);
                    if (response.command === MONITORING_COMMAND.MONITORING_ENGINE) {
                        setEngineState({
                            isStarted: response.attributes.isStarted,
                            user: response.attributes.user,
                            dateTimeStamp: response.attributes.dateTimeStamp
                        });
                    }
                }
                //end
                onmessage(data);
            };
        }
    }

    const sendMessage = (data: any) => {
        if (websocket && websocket.readyState === websocket.OPEN) {
            websocket.send(data);
        }
    }

    return <IcaWebsocketContext.Provider value={{ user, setOnMessage, startStopTopic: startStopTopic, disposeConnection, connectionState, sendMessage, initializeConnection, stopAllTopics, engineState }}>
        {children}
    </IcaWebsocketContext.Provider>
}
